// Workflow block library + settings schemas + pre-built templates for the visual builder.

export const KIND_COLOR = {
  trigger: { bg: "#ecfdf5", border: "#16a34a", dot: "#16a34a", text: "#14532d" },
  action: { bg: "#eff6ff", border: "#2563eb", dot: "#2563eb", text: "#1e3a8a" },
  condition: { bg: "#fffbeb", border: "#d97706", dot: "#d97706", text: "#7c2d12" },
};

export const BLOCKS = {
  trigger: [
    { type: "trigger_new_lead", label: "New Lead Added" },
    { type: "trigger_webhook_received", label: "Webhook / API Received" },
    { type: "trigger_call_answered", label: "Call Answered" },
    { type: "trigger_call_not_answered", label: "Call Not Answered" },
    { type: "trigger_lead_qualified", label: "Lead Qualified" },
    { type: "trigger_not_interested", label: "Lead Not Interested" },
    { type: "trigger_wa_reply", label: "WhatsApp Reply Received" },
    { type: "trigger_demo_booked", label: "Demo Booked" },
    { type: "trigger_time_based", label: "Time Based" },
    { type: "trigger_score_threshold", label: "Score Threshold Reached" },
    { type: "trigger_campaign_started", label: "Campaign Started" },
  ],
  action: [
    { type: "action_call", label: "Make Outbound Call" },
    { type: "action_send_webhook", label: "Send Webhook / API Result" },
    { type: "action_whatsapp", label: "Send WhatsApp Message" },
    { type: "action_email", label: "Send Email" },
    { type: "action_wait", label: "Wait / Delay" },
    { type: "action_update_status", label: "Update Lead Status" },
    { type: "action_update_score", label: "Update Lead Score" },
    { type: "action_assign_campaign", label: "Assign to Campaign" },
    { type: "action_wa_template", label: "Send WhatsApp Template" },
    { type: "action_alert_team", label: "Alert Sales Team" },
    { type: "action_book_slot", label: "Book Consultation Slot" },
    { type: "action_email_nurture", label: "Add to Email Nurture" },
    { type: "action_remove_campaign", label: "Remove from Campaign" },
    { type: "action_mark_dnc", label: "Mark as DNC" },
  ],
  condition: [
    { type: "cond_score_gte", label: "If Score >= X" },
    { type: "cond_score_lt", label: "If Score < X" },
    { type: "cond_language", label: "If Language =" },
    { type: "cond_city", label: "If City =" },
    { type: "cond_attempts", label: "If Call Attempts >= X" },
    { type: "cond_status", label: "If Lead Status =" },
    { type: "cond_wa_contains", label: "If WhatsApp Reply Contains" },
    { type: "cond_time", label: "If Time Is" },
  ],
};

export const KIND_OF = {};
Object.entries(BLOCKS).forEach(([kind, arr]) => arr.forEach((b) => (KIND_OF[b.type] = kind)));

const STATUS_OPTS = ["Pending", "Calling", "Qualified", "Not Interested", "Demo Booked", "Converted", "Disqualified", "Follow Up", "No Response"];
const TIMING = [{ v: "immediate", l: "Immediately" }, { v: "delay", l: "After a delay" }, { v: "specific", l: "At a specific time" }];

// Settings schema per block type. Each field: {key,label,type,options?,placeholder?}
export const SETTINGS = {
  action_call: [
    { key: "call_time", label: "Call time", type: "select", options: TIMING },
    { key: "delay_hours", label: "Delay (hours)", type: "number" },
    { key: "max_attempts", label: "Max attempts (1-3)", type: "number" },
    { key: "calling_window", label: "Calling window", type: "select", options: [{ v: "campaign", l: "Campaign default" }, { v: "custom", l: "Custom" }] },
  ],
  action_whatsapp: [
    { key: "message_type", label: "Message type", type: "select", options: [{ v: "custom", l: "Custom text" }, { v: "template", l: "Template" }] },
    { key: "text", label: "Custom text", type: "textarea", placeholder: "Hi {lead_name}, about {interest}..." },
    { key: "template", label: "Template name", type: "text" },
    { key: "timing", label: "Send timing", type: "select", options: TIMING },
    { key: "delay_hours", label: "Delay (hours)", type: "number" },
  ],
  action_wa_template: [
    { key: "template", label: "Template name", type: "text" },
    { key: "timing", label: "Send timing", type: "select", options: TIMING },
    { key: "delay_hours", label: "Delay (hours)", type: "number" },
  ],
  action_email: [
    { key: "subject", label: "Subject line", type: "text", placeholder: "Hi {lead_name}..." },
    { key: "body", label: "Email body", type: "textarea" },
    { key: "sender", label: "Sender name", type: "text" },
    { key: "timing", label: "Send timing", type: "select", options: TIMING },
    { key: "delay_hours", label: "Delay (hours)", type: "number" },
  ],
  action_email_nurture: [{ key: "list_name", label: "Nurture list", type: "text" }],
  action_wait: [
    { key: "amount", label: "Delay amount", type: "number" },
    { key: "unit", label: "Unit", type: "select", options: [{ v: "minutes", l: "Minutes" }, { v: "hours", l: "Hours" }, { v: "days", l: "Days" }] },
    { key: "until_time", label: "Wait until (time of day, optional)", type: "text", placeholder: "e.g. 10:00" },
  ],
  action_update_status: [{ key: "status", label: "New status", type: "select", options: STATUS_OPTS.map((s) => ({ v: s, l: s })) }],
  action_update_score: [{ key: "score", label: "Set score to", type: "number" }],
  action_assign_campaign: [{ key: "campaign", label: "Campaign", type: "text" }],
  action_remove_campaign: [{ key: "campaign", label: "Campaign", type: "text" }],
  action_alert_team: [
    { key: "channel", label: "Channel", type: "select", options: [{ v: "whatsapp", l: "WhatsApp" }, { v: "slack", l: "Slack" }, { v: "both", l: "Both" }] },
    { key: "message", label: "Message", type: "textarea", placeholder: "Hot lead: {lead_name} ({mobile}) score {score}" },
    { key: "recipients", label: "Recipients", type: "select", options: [{ v: "managers", l: "All managers" }, { v: "sales_group", l: "Sales team group" }, { v: "specific", l: "Specific user" }] },
  ],
  action_book_slot: [{ key: "calendar", label: "Calendar / link", type: "text" }],
  action_mark_dnc: [{ key: "reason", label: "Reason (optional)", type: "text" }],
  // triggers with config
  trigger_score_threshold: [{ key: "threshold", label: "Score >=", type: "number" }],
  trigger_lead_qualified: [{ key: "threshold", label: "Qualified score >=", type: "number" }],
  trigger_time_based: [{ key: "schedule", label: "Schedule (cron / time)", type: "text", placeholder: "e.g. daily 09:00" }],
  trigger_new_lead: [{ key: "source", label: "Lead source filter (optional)", type: "text", placeholder: "e.g. Inbound" }],
  trigger_webhook_received: [{ key: "note", label: "Note (optional)", type: "text", placeholder: "e.g. IndiaMART new lead" }],
  action_send_webhook: [
    { key: "url", label: "Webhook URL (https)", type: "text", placeholder: "https://hooks.zapier.com/..." },
    { key: "method", label: "Method", type: "select", options: [{ v: "POST", l: "POST" }, { v: "PUT", l: "PUT" }] },
    { key: "secret", label: "Secret (optional, for HMAC signature)", type: "text" },
  ],
};

// Conditions share a schema
const COND = [
  { key: "field", label: "Field to check", type: "select", options: ["Score", "Language", "City", "Call Attempts", "Status", "WhatsApp Reply", "Time"].map((v) => ({ v, l: v })) },
  { key: "operator", label: "Operator", type: "select", options: [">=", "<=", "=", "contains"].map((v) => ({ v, l: v })) },
  { key: "value", label: "Value to compare", type: "text" },
];
Object.keys(KIND_OF).filter((t) => KIND_OF[t] === "condition").forEach((t) => (SETTINGS[t] = COND));

export const getSettingsSchema = (type) => SETTINGS[type] || [{ key: "note", label: "Note", type: "textarea" }];

// ---- Pre-built templates (compact node/edge graphs) ----
let _c = 0;
const nid = () => `n${Date.now()}_${_c++}`;
const mk = (type, label, x, y, settings = {}) => ({ id: nid(), type: "block", position: { x, y }, data: { type, label, kind: KIND_OF[type], settings } });
const link = (s, t, sh, lbl) => ({ id: `e_${s}_${t}_${sh || "o"}`, source: s, target: t, sourceHandle: sh, label: lbl, animated: false });

export const TEMPLATES = [
  {
    id: "standard_followup", name: "Standard Follow-up",
    desc: "New lead → wait → call → branch by answer & score.",
    build() {
      const a = mk("trigger_new_lead", "New Lead Added", 40, 220);
      const b = mk("action_wait", "Wait 30 min", 300, 220, { amount: 30, unit: "minutes" });
      const c = mk("action_call", "Make Outbound Call", 560, 220, { call_time: "immediate", max_attempts: 1 });
      const d = mk("cond_score_gte", "If Score >= 80", 820, 100, { field: "Score", operator: ">=", value: "80" });
      const e = mk("action_alert_team", "Alert Sales + Book", 1080, 60, { channel: "both" });
      const f = mk("action_whatsapp", "Send WhatsApp (proposal)", 1080, 200, { message_type: "custom" });
      const g = mk("action_email_nurture", "Add to Email Nurture", 820, 360, { list_name: "cold" });
      return { nodes: [a, b, c, d, e, f, g], edges: [link(a.id, b.id), link(b.id, c.id), link(c.id, d.id, null, "answered"), link(c.id, g.id, null, "not answered"), link(d.id, e.id, "true", "yes"), link(d.id, f.id, "false", "no")] };
    },
  },
  {
    id: "hot_fast_track", name: "Hot Lead Fast Track",
    desc: "Score >= 80 → alert + WhatsApp demo link → follow up.",
    build() {
      const a = mk("trigger_score_threshold", "Score Threshold (>=80)", 40, 160, { threshold: 80 });
      const b = mk("action_alert_team", "Alert Sales Team", 320, 160, { channel: "both" });
      const c = mk("action_whatsapp", "Send WhatsApp (demo link)", 600, 160, { message_type: "template" });
      const d = mk("action_wait", "Wait 2 hours", 880, 160, { amount: 2, unit: "hours" });
      const e = mk("action_call", "Make Outbound Call", 1160, 160, { call_time: "immediate" });
      return { nodes: [a, b, c, d, e], edges: [link(a.id, b.id), link(b.id, c.id), link(c.id, d.id), link(d.id, e.id)] };
    },
  },
  {
    id: "long_nurture", name: "Long Nurture (Cold)",
    desc: "Cold leads → 3 emails over days → final call.",
    build() {
      const a = mk("trigger_new_lead", "Lead Score < 50", 40, 160, { source: "" });
      const b = mk("action_email", "Email — Introduction", 300, 160, { subject: "Hello {lead_name}" });
      const c = mk("action_wait", "Wait 3 days", 560, 160, { amount: 3, unit: "days" });
      const d = mk("action_email", "Email — Case Study", 820, 160, {});
      const e = mk("action_wait", "Wait 7 days", 1080, 160, { amount: 7, unit: "days" });
      const f = mk("action_call", "Make Outbound Call", 1340, 160, {});
      return { nodes: [a, b, c, d, e, f], edges: [link(a.id, b.id), link(b.id, c.id), link(c.id, d.id), link(d.id, e.id), link(e.id, f.id)] };
    },
  },
  {
    id: "wa_reengage", name: "WhatsApp Re-engagement",
    desc: "No response 7 days → WhatsApp → branch on reply.",
    build() {
      const a = mk("trigger_time_based", "No Response 7 days", 40, 200, { schedule: "7d no response" });
      const b = mk("action_whatsapp", "WhatsApp re-engage", 320, 200, { message_type: "custom" });
      const c = mk("cond_wa_contains", "If Reply Received", 600, 200, { field: "WhatsApp Reply", operator: "contains", value: "" });
      const d = mk("action_call", "Call within 2 hours", 880, 120, {});
      const e = mk("action_mark_dnc", "Mark Disqualified", 880, 300, {});
      return { nodes: [a, b, c, d, e], edges: [link(a.id, b.id), link(b.id, c.id), link(c.id, d.id, "true", "reply"), link(c.id, e.id, "false", "no reply")] };
    },
  },
  {
    id: "inbound_response", name: "Inbound Lead Response",
    desc: "Inbound lead → thank-you WhatsApp → quick call.",
    build() {
      const a = mk("trigger_new_lead", "New Lead (Inbound)", 40, 180, { source: "Inbound" });
      const b = mk("action_whatsapp", "WhatsApp thank-you", 320, 180, { message_type: "custom", timing: "immediate" });
      const c = mk("action_wait", "Wait 5 min", 600, 180, { amount: 5, unit: "minutes" });
      const d = mk("action_call", "Make Outbound Call", 860, 180, {});
      const e = mk("action_book_slot", "Book Consultation", 1120, 180, {});
      return { nodes: [a, b, c, d, e], edges: [link(a.id, b.id), link(b.id, c.id), link(c.id, d.id), link(d.id, e.id, null, "answered ≥50")] };
    },
  },
];
